import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import BaseModal from '../components/modals/BaseModal';

const Integrations = () => {
  const { integrations, toggleIntegration } = useCRM();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  // Added Categories
  const categories = ['Alle', 'Social Media', 'Email & Calendar', 'Communication', 'Accounting'];

  const filteredIntegrations = integrations.filter(int => {
    const matchesSearch = int.name.toLowerCase().includes(searchQuery.toLowerCase()) || int.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'Alle' || int.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'linkedin': return '👔';
      case 'facebook': return '📘';
      case 'instagram': return '📸';
      case 'x': return '𝕏';
      case 'tiktok': return '🎵';
      case 'pinterest': return '📌';
      case 'google': return 'G'; // Updated Google icon
      case 'microsoft': return '🪟'; // Added Microsoft icon
      case 'visma': return '💼';
      default: return '🔗';
    }
  };

  const handleConnect = (e) => {
    e.preventDefault();
    toggleIntegration(selectedIntegration.id);
    setIsConfigModalOpen(false);
  };

  // Special view for Social Media matching Image 4
  if (activeCategory === 'Social Media') {
    const socialInts = integrations.filter(i => i.category === 'Social Media');
    return (
      <div className="p-8 max-w-4xl mx-auto space-y-8 min-h-screen">
        <div className="flex gap-2 mb-8">
          {categories.map(cat => (
            <button 
              key={cat} 
              onClick={() => setActiveCategory(cat)} 
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeCategory === cat ? 'bg-slate-900 text-white' : 'bg-white border text-slate-600'}`}>{cat}</button>
          ))}
        </div>
        
        {/* Match Image 4 UI */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-xl font-bold text-slate-900">Connect Socials</h2>
            <p className="text-sm text-slate-500 mt-1">Connect your social media accounts to allow publishing on your behalf.</p>
          </div>
          <div className="divide-y divide-slate-100">
            {socialInts.map(int => (
              <div key={int.id} className="flex flex-col sm:flex-row items-center justify-between p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4 w-full sm:w-auto mb-4 sm:mb-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl shadow-sm ${int.connected ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
                    <SafeIcon icon={getIcon(int.icon)} />
                  </div>
                  <span className="font-medium text-slate-800">{int.name}</span>
                </div>
                <button 
                  onClick={() => toggleIntegration(int.id)}
                  className={`w-full sm:w-auto px-6 py-2 rounded-lg text-sm font-bold transition-all ${int.connected ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm'}`}
                >
                  {int.connected ? 'Disconnect' : 'Connect'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default Grid View for other categories
  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Categories Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-2 px-2 custom-scrollbar">
        {categories.map(category => (
          <button 
            key={category} 
            onClick={() => setActiveCategory(category)}
            className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${activeCategory === category ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredIntegrations.map((int) => (
            <motion.div 
              layout
              key={int.id} 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col"
            >
              <div className="flex justify-between items-start mb-6">
                <div className={`p-4 rounded-[1.5rem] ${int.connected ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'}`}>
                  <SafeIcon icon={getIcon(int.icon)} className="w-8 h-8" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-black text-slate-900 mb-2">{int.name}</h3>
                <p className="text-sm text-slate-500 font-medium mb-6">{int.description}</p>
              </div>
              <button 
                onClick={() => toggleIntegration(int.id)}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${int.connected ? 'bg-slate-100 text-slate-600' : 'bg-blue-600 text-white shadow-md hover:bg-blue-700'}`}
              >
                {int.connected ? 'Koble fra' : 'Koble til'}
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Integrations;